import { MoldAutocomplete } from "@/shared/autocompletes/MoldAutocomplete";
import { modalStyles } from "@/shared/styles/modalStyles";
import { ErrorModal, MesfModal, getMomentTz } from "@dexteel/mesf-core";
import { Alert, Box, Grid, TextField, Typography } from "@mui/material";
import { NotificationSnackBar } from "../../../../../../controls/snackbars/notification-snackbar";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../../controls/buttons/FooterModalButtons";
import { DateTimePickerDE } from "../../../../../../shared/data-entries/common/DateTimePickerDE";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useCasterDataEntryActions } from "../../../hooks/useCasterDataEntryActions";
import { Strand } from "../../../models/caster.models";

type Props = {
  show: boolean;
  onHide: () => void;
  strand: Strand;
};

const moment = getMomentTz();

export const EditStrand = ({ show, onHide, strand }: Props) => {
  const {
    state: { errorMessage, deProcess },
    actions: { setErrorMessage },
  } = useDataEntryContext();
  const { saveStrand, reassignMold } = useCasterDataEntryActions();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState<string | null>(
    null,
  );

  const {
    getValues,
    handleSubmit,
    setValue,
    control,
    reset,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<Strand>({});

  const firstCutTime = watch("FirstCutTime");
  const selectedMoldId = watch("MoldId");

  const conflictStrand =
    selectedMoldId && selectedMoldId !== strand.MoldId
      ? deProcess?.strands.find(
          (s) => s.MoldId === selectedMoldId && s.Strand !== strand.Strand,
        )
      : undefined;

  const onSubmit: SubmitHandler<Strand> = async (data) => {
    setIsSubmitLoading(true);

    if (conflictStrand) {
      await reassignMold({
        sourceStrand: conflictStrand.Strand,
        targetStrand: data.Strand,
        moldId: data.MoldId!,
      });
    } else {
      await saveStrand(data);
    }

    setIsSubmitLoading(false);
    onHide();
  };

  useEffect(() => {
    if (show) {
      reset(strand);
    }
  }, [show]);

  return (
    <>
      <Grid container>
        <Grid item>
          <MesfModal
            title={`EDIT MANUAL STRAND # ${strand?.Strand}`}
            open={show}
            handleClose={() => onHide()}
            id="edit-strand-modal"
            maxWidth="sm"
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <MesfModal.Content dividers sx={modalStyles.modalContent}>
                <Grid container spacing={1}>
                  {conflictStrand && (
                    <Grid item xs={12}>
                      <Alert severity="warning">
                        <Typography variant="body2">
                          <strong>{conflictStrand.MoldName}</strong> is
                          currently on Strand {conflictStrand.Strand}. Saving
                          will move it to Strand {strand?.Strand}, leaving
                          Strand {conflictStrand.Strand} without a mold.
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="MoldId"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <MoldAutocomplete
                          label="Mold"
                          value={field.value}
                          onChange={field.onChange}
                          fieldError={error}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="FirstCutTime"
                      control={control}
                      defaultValue={strand?.FirstCutTime}
                      rules={{
                        validate: {
                          afterLadleOpen: (value) => {
                            const ladleOpen = moment
                              .utc(deProcess?.process?.LadleOpen)
                              .toDate();
                            if (!value || !ladleOpen) return true;
                            return (
                              !ladleOpen ||
                              value >= ladleOpen ||
                              "First Cut Time must be after Ladle Open"
                            );
                          },
                        },
                      }}
                      render={({ field }) => (
                        <DateTimePickerDE
                          {...field}
                          format={"HH:mm"}
                          disableOpenPicker
                          value={getValues("FirstCutTime")}
                          label="First Cut Time"
                          onChange={(val) => {
                            clearErrors("FirstCutTime");
                            setValue("FirstCutTime", val);
                          }}
                          onBlur={() => {}}
                        />
                      )}
                    />
                    {errors.FirstCutTime && (
                      <Box component="span" sx={modalStyles.errorLabel}>
                        {errors.FirstCutTime.message}
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="LastCutTime"
                      control={control}
                      defaultValue={strand?.LastCutTime}
                      rules={{
                        validate: {
                          afterFirstCutTime: (value) => {
                            if (!value || !firstCutTime) return true;
                            return (
                              !firstCutTime ||
                              value > firstCutTime ||
                              "Last Cut Time must be after First Cut Time"
                            );
                          },
                        },
                      }}
                      render={({ field }) => (
                        <DateTimePickerDE
                          {...field}
                          format={"HH:mm"}
                          disableOpenPicker
                          label="Last Cut Time"
                          onBlur={() => {}}
                        />
                      )}
                    />
                    {errors.LastCutTime && (
                      <Box component="span" sx={modalStyles.errorLabel}>
                        {errors.LastCutTime.message}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="SpeedIPMAvg"
                      control={control}
                      defaultValue={strand?.SpeedIPMAvg}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          variant="outlined"
                          fullWidth
                          label="Speed IPM Avg"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="WaterDeltaTAvg"
                      control={control}
                      defaultValue={strand?.WaterDeltaTAvg}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          variant="outlined"
                          fullWidth
                          label="Water Delta Temp Avg"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="WaterFlowAvg"
                      control={control}
                      defaultValue={strand?.WaterFlowAvg}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          variant="outlined"
                          fullWidth
                          label="Water Flow Avg"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="WaterTempAvg"
                      control={control}
                      defaultValue={strand?.WaterTempAvg}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          variant="outlined"
                          fullWidth
                          label="Water Temp Avg"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </MesfModal.Content>
              <MesfModal.Actions sx={modalStyles.modalActions}>
                <FooterModalButtons
                  isSubmitLoading={isSubmitLoading}
                  onHide={onHide}
                />
              </MesfModal.Actions>
            </form>
          </MesfModal>
          <NotificationSnackBar
            message={notificationMessage}
            onHide={() => setNotificationMessage(null)}
            severity="success"
          />
        </Grid>
      </Grid>
      <ErrorModal
        error={errorMessage}
        onHide={() => setErrorMessage("")}
        title="Error Editing Manual Strand"
      />
    </>
  );
};
